import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Calendar } from 'lucide-react';
import { restaurantInfo } from '../data/mock';

const ReservationSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="reservation"
      ref={sectionRef}
      className="py-24 md:py-32 bg-black text-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-block bg-white text-black px-4 py-2 rounded-md text-sm font-semibold mb-6">
              📍 Réservation
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-8 leading-tight">
              Entrez dans l'expérience
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Réservez votre table et laissez-vous porter par une atmosphère unique,
              où chaque soirée devient un souvenir.
            </p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Info Cards */}
            <div
              className={`space-y-6 transform transition-all duration-1000 delay-200 ${
                isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
              }`}
            >
              {/* Address Card */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Notre Adresse</h3>
                    <p className="text-gray-300 leading-relaxed">
                      {restaurantInfo.address}
                    </p>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(restaurantInfo.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-sm font-medium text-white hover:text-gray-300 underline transition-colors duration-200"
                    >
                      Voir sur Google Maps
                    </a>
                  </div>
                </div>
              </div>

              {/* Phone Card */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Appelez-nous</h3>
                    <a
                      href={`tel:${restaurantInfo.phone}`}
                      className="text-gray-300 hover:text-white transition-colors duration-200 text-lg"
                    >
                      {restaurantInfo.phone}
                    </a>
                    <p className="text-sm text-gray-400 mt-2">
                      Du lundi au dimanche, 12h - 2h
                    </p>
                  </div>
                </div>
              </div>

              {/* Hours Card */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar size={24} className="text-black" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3">Horaires</h3>
                    <div className="space-y-2 text-gray-300">
                      <div className="flex justify-between">
                        <span>Déjeuner</span>
                        <span className="font-medium">12h00 - 15h00</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Dîner</span>
                        <span className="font-medium">19h00 - 23h00</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Bar</span>
                        <span className="font-medium">Jusqu'à 2h00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div
              className={`transform transition-all duration-1000 delay-400 ${
                isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
              }`}
            >
              <div className="bg-white rounded-2xl p-10 shadow-2xl">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-full mb-6">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-black mb-4">
                    Réserver maintenant
                  </h3>
                  <p className="text-gray-700 mb-8 leading-relaxed">
                    Sécurisez votre place dans l'un des lieux les plus prisés de Nice.
                    Réservation en ligne simple et rapide.
                  </p>
                  <a
                    href={restaurantInfo.reservationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block w-full bg-black text-white px-8 py-5 rounded-lg font-semibold text-lg hover:bg-gray-800 transform hover:-translate-y-1 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Réserver votre table
                  </a>
                  <p className="text-sm text-gray-500 mt-4">
                    Confirmation immédiate • Annulation gratuite
                  </p>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="mt-8 rounded-xl overflow-hidden shadow-xl h-64">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(restaurantInfo.address)}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="La Grande Brune Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReservationSection;
